/**
 * Mock product list for /calculator-v3 preview.
 *
 * Lifted verbatim from docs/audits/calculator-redesign-2026-05-05/
 * calculator-comprehensive-mockup.html (F3 match cards + F4 grid).
 *
 * NOT WIRED to the rate-feed API. When the v3 preview is promoted to
 * production, replace this file with a service call to
 * RateApiService.getIaProducts(), filtered to the user's shelf.
 */

export interface MockProduct {
  /** Stable id used for routing handoffs (?product=...) */
  id: string;
  /** Optional — only set on the static mock dataset. Real ranked cards omit this. */
  rank?: number;
  /** GMWB rider id from the Benefit API. Present on real ranked cards
   *  (RealRankedCard inherits it from RiderWithFit/RiderResult). */
  gmwbId?: number;
  /** Rider result row id (RiderResult.id) — required by GetIllustration.
   *  Distinct from gmwbId. Present on real ranked cards; absent on the
   *  static mock dataset. F5 uses this to pull the year-by-year ledger. */
  riderRowId?: number;
  carrier: string;
  carrierSlug: string;
  /** Carrier + product display name as shown on cards */
  productName: string;
  /** Rider tail shown after the + on cards/rows */
  rider: string;
  productType: 'IA' | 'FA' | 'RILA' | 'VA';
  amBest: string;
  /** Year-1 income in dollars */
  y1Income: number;
  /** Income base in dollars at start age */
  incomeBase: number;
  /** Payout % at start age (single life) */
  payoutPct: number;
  /** Rider charge as % of benefit base */
  riderCharge: number;
  /** Optional fit score for V2 stage handoff */
  fitScore?: number;
}

export const MOCK_PRODUCTS: MockProduct[] = [
  {
    id: 'athene-ascent-10',
    rank: 1,
    carrier: 'Athene',
    carrierSlug: 'athene',
    productName: 'Athene Ascent Pro 10',
    rider: 'Income Rider',
    productType: 'IA',
    amBest: 'A+',
    y1Income: 14800,
    incomeBase: 337500,
    payoutPct: 6.34,
    riderCharge: 1.10,
    fitScore: 94,
  },
  {
    id: 'allianz-benefit-control',
    rank: 2,
    carrier: 'Allianz',
    carrierSlug: 'allianz',
    productName: 'Allianz Benefit Control',
    rider: 'Income',
    productType: 'IA',
    amBest: 'A+',
    y1Income: 14520,
    incomeBase: 330000,
    payoutPct: 6.35,
    riderCharge: 1.25,
    fitScore: 91,
  },
  {
    id: 'nationwide-new-heights',
    rank: 3,
    carrier: 'Nationwide',
    carrierSlug: 'nationwide',
    productName: 'Nationwide New Heights',
    rider: 'Lifetime Income',
    productType: 'IA',
    amBest: 'A+',
    y1Income: 14180,
    incomeBase: 325000,
    payoutPct: 6.22,
    riderCharge: 0.95,
    fitScore: 88,
  },
  {
    id: 'fg-power-series',
    rank: 4,
    carrier: 'F&G',
    carrierSlug: 'fg',
    productName: 'F&G Power Series',
    rider: 'Income Builder',
    productType: 'IA',
    amBest: 'A',
    y1Income: 13940,
    incomeBase: 321000,
    payoutPct: 6.18,
    riderCharge: 1.00,
  },
  {
    id: 'lincoln-optiblend',
    rank: 5,
    carrier: 'Lincoln',
    carrierSlug: 'lincoln',
    productName: 'Lincoln OptiBlend',
    rider: 'Lifetime Income',
    productType: 'IA',
    amBest: 'A+',
    y1Income: 13560,
    incomeBase: 315000,
    payoutPct: 6.10,
    riderCharge: 1.15,
  },
  {
    id: 'symetra-edge-pro',
    rank: 6,
    carrier: 'Symetra',
    carrierSlug: 'symetra',
    productName: 'Symetra Edge Pro',
    rider: 'Income',
    productType: 'IA',
    amBest: 'A',
    y1Income: 13210,
    incomeBase: 308500,
    payoutPct: 6.04,
    riderCharge: 1.05,
  },
  {
    id: 'global-atlantic-choice',
    rank: 7,
    carrier: 'Global Atlantic',
    carrierSlug: 'global-atlantic',
    productName: 'Global Atlantic Choice',
    rider: 'Income',
    productType: 'IA',
    amBest: 'A',
    y1Income: 12940,
    incomeBase: 302000,
    payoutPct: 5.96,
    riderCharge: 1.20,
  },
  {
    id: 'brighthouse-securekey',
    rank: 8,
    carrier: 'Brighthouse',
    carrierSlug: 'brighthouse',
    productName: 'Brighthouse SecureKey',
    rider: 'Lifetime',
    productType: 'IA',
    amBest: 'A',
    y1Income: 12680,
    incomeBase: 298000,
    payoutPct: 5.88,
    riderCharge: 1.30,
  },
];

/** Total number of products on Marcus's shelf (mockup says 23). */
export const MOCK_SHELF_TOTAL = 23;
